import type React from 'react'
import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Filter as badWordsFilterEng } from 'bad-words'
import badWordsFilterKor from 'badwords-ko'

import { WelcomeScreen } from './WelcomeScreen'
import { SignupProgress } from './SignupProgress'
import { NicknameStep } from './signupSteps/NicknameStep'
import { GenderStep } from './signupSteps/GenderStep'
import { BirthDateStep } from './signupSteps/BirthDateStep'
import { GradeStep } from './signupSteps/GradeStep'
import { SchoolStep } from './signupSteps/SchoolStep'
import { MbtiStep } from './signupSteps/MBTIStep'
import { VltTypeStep } from './signupSteps/VltTypeStep'
import { SignupCompleteScreen } from './signupSteps/SignupCompleteScreen'

import WideButton from '@/components/common/button/WideButton'
import type { GradeCode } from '@/api/model/userType'
import { getTodayDate } from '@/utils/dateUtils'

export type Provider = 'kakao' | 'google' | 'apple'

export interface SignupData {
  nickname: string
  gender: string
  birthDate: string
  grade: GradeCode | null
  schoolName: string
  schoolCode: string
  mbti: string
  privacyConsent: boolean
  user_id: string
  vltType: number
}

export interface TermCheckboxes {
  all: boolean
  required1: boolean
  required2: boolean
}

export type TermKey = 'required1' | 'required2'

export interface TermDetail {
  title: string
}

export interface TermDetails {
  required1: TermDetail
  required2: TermDetail
}

interface SignupFormProps {
  initialSignupData?: Partial<SignupData>
  onComplete: (data: SignupData) => void
}

// ========================================
// 스텝 ID 상수 정의
// ========================================
const STEP_ID = {
  WELCOME: 'welcome',
  NICKNAME: 'nickname',
  GENDER: 'gender',
  BIRTH_DATE: 'birthDate',
  GRADE: 'grade',
  VLT_TYPE: 'vltType',
  SCHOOL: 'school',
  MBTI: 'mbti',
  COMPLETE: 'complete',
} as const

type StepId = (typeof STEP_ID)[keyof typeof STEP_ID]

// 학교 선택을 건너뛰는 학년 코드
const GRADES_SKIP_SCHOOL: GradeCode[] = ['g13', 'g14', 'g15', 'g16', 'g17']

// ========================================
// 스텝 설정 타입 정의
// ========================================
interface StepConfig {
  /** 스텝 활성화 여부 (false면 스텝을 건너뜀) */
  enabled: boolean
  /** 버튼 텍스트 */
  buttonText: string
  /** "다음에 알려줄게" 스킵 버튼 표시 여부 */
  showSkipButton?: boolean
  /** 조건부 스킵 (특정 조건에서 스텝 건너뛰기) */
  shouldSkip?: (data: SignupData) => boolean
}

// ========================================
// 스텝 설정 (스텝 추가/제거/수정 시 이곳만 수정)
// ========================================
const STEP_CONFIG: Record<StepId, StepConfig> = {
  [STEP_ID.WELCOME]: {
    enabled: true,
    buttonText: '다음',
  },
  [STEP_ID.NICKNAME]: {
    enabled: true,
    buttonText: '이렇게 불러주면 돼!',
  },
  [STEP_ID.GENDER]: {
    enabled: true,
    buttonText: '내 성별을 골랐어!',
  },
  [STEP_ID.BIRTH_DATE]: {
    enabled: true,
    buttonText: '이 날이 내 인생 첫 날이야!',
  },
  [STEP_ID.GRADE]: {
    enabled: true,
    buttonText: '좋아 골랐어!',
  },
  [STEP_ID.VLT_TYPE]: {
    enabled: true,
    buttonText: '나의 타입 선택 완료!',
  },
  [STEP_ID.SCHOOL]: {
    enabled: true,
    buttonText: '나는 여기에 다녀!',
    showSkipButton: true,
    shouldSkip: (data) => !!(data.grade && GRADES_SKIP_SCHOOL.includes(data.grade)),
  },
  [STEP_ID.MBTI]: {
    enabled: true,
    buttonText: '내 MBTI는 이거야!',
    showSkipButton: true,
  },
  [STEP_ID.COMPLETE]: {
    enabled: true,
    buttonText: '다음',
  },
}

// ========================================
// 스텝 순서 정의 (순서 변경 시 이 배열만 수정)
// ========================================
const STEP_ORDER: StepId[] = [
  STEP_ID.WELCOME,
  STEP_ID.NICKNAME,
  STEP_ID.GENDER,
  STEP_ID.BIRTH_DATE,
  STEP_ID.GRADE,
  STEP_ID.VLT_TYPE,
  STEP_ID.SCHOOL,
  STEP_ID.MBTI,
  STEP_ID.COMPLETE,
]

// 활성화된 스텝만 필터링
const getEnabledSteps = (): StepId[] => STEP_ORDER.filter(stepId => STEP_CONFIG[stepId].enabled)

// 실제 입력 스텝 수 (웰컴, 완료 제외, 활성화된 것만)
const getTotalInputSteps = (): number =>
  getEnabledSteps().filter(step => step !== STEP_ID.WELCOME && step !== STEP_ID.COMPLETE).length

const nicknameFilterEng = new badWordsFilterEng()
const nicknameFilterKor = new badWordsFilterKor()

// 닉네임 검증 함수
const validateNickname = (
  nickname: string
): {
  valid: boolean
  error: string
} => {
  if (nickname.length < 2) {
    if (nickname.length > 0) {
      return { valid: false, error: '두 글자 이상으로 설정해주세요!' }
    }
    return { valid: false, error: '' } // No error message if empty
  }

  const lowerNickname = nickname.toLowerCase()
  const filteredValueEng = nicknameFilterEng.clean(lowerNickname)
  const filteredValueKor = nicknameFilterKor.clean(lowerNickname)

  if (filteredValueEng.includes('*') || filteredValueKor.includes('*')) {
    return { valid: false, error: '부적절한 단어가 포함되어 있습니다.' }
  }

  if (['admin', 'administrator', 'root', 'system', 'null', 'undefined'].some(word => lowerNickname.includes(word))) {
    return { valid: false, error: '사용할 수 없는 닉네임입니다.' }
  }

  return { valid: true, error: '' }
}

// 생년월일 검증 함수
const validateBirthDate = (birthDate: string): boolean => {
  if (!birthDate) return false

  const today = new Date()
  const selectedDate = new Date(birthDate)

  // 미래 날짜 불가
  if (selectedDate > today) return false

  // 100년 전보다 이전 날짜 불가
  const hundredYearsAgo = new Date()
  hundredYearsAgo.setFullYear(today.getFullYear() - 100)
  if (selectedDate < hundredYearsAgo) return false

  return true

  // 추가 검증: 너무 어린 나이 (5세 이하) 불가 - 추후에 연령 제한
  // const age = today.getFullYear() - selectedDate.getFullYear()
  // const monthDiff = today.getMonth() - selectedDate.getMonth()
  // const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate()) ? age - 1 : age

  // return actualAge >= 5 && actualAge <= 100
}

// ========================================
// 헬퍼 함수: 스텝 인덱스 <-> 스텝 ID 변환
// ========================================
const getEnabledStepId = (index: number): StepId => getEnabledSteps()[index] ?? STEP_ID.WELCOME

// 특정 스텝을 건너뛰어야 하는지 확인 (비활성화 또는 조건부 스킵)
const shouldSkipStep = (stepId: StepId, data: SignupData): boolean => {
  const config = STEP_CONFIG[stepId]
  // 비활성화된 스텝은 건너뜀
  if (!config.enabled) return true
  // 조건부 스킵 확인
  if (config.shouldSkip?.(data)) return true
  return false
}

// 첫 번째 입력 스텝 인덱스 가져오기 (웰컴 다음)
const getFirstInputStepIndex = (): number => {
  const enabledSteps = getEnabledSteps()
  const nicknameIndex = enabledSteps.indexOf(STEP_ID.NICKNAME)
  return nicknameIndex >= 0 ? nicknameIndex : 1
}

export function SignupForm({ initialSignupData, onComplete }: SignupFormProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [stepIndex, setStepIndex] = useState<number>(0)
  const [showWelcomeTransition, setShowWelcomeTransition] = useState<boolean>(true)

  const [signupData, setSignupData] = useState<SignupData>({
    nickname: initialSignupData?.nickname || '',
    gender: initialSignupData?.gender || '',
    birthDate: initialSignupData?.birthDate || getTodayDate(),
    grade: initialSignupData?.grade || null,
    schoolName: initialSignupData?.schoolName || '',
    schoolCode: initialSignupData?.schoolCode || '',
    mbti: initialSignupData?.mbti || '',
    privacyConsent: initialSignupData?.privacyConsent || false,
    user_id: initialSignupData?.user_id || '',
    vltType: initialSignupData?.vltType || 0
  })

  // 학교가 실제로 선택되었는지 추적
  const [isSchoolSelected, setIsSchoolSelected] = useState(false)

  // 활성화된 스텝 목록
  const enabledSteps = getEnabledSteps()
  const firstInputStepIndex = getFirstInputStepIndex()
  const totalInputSteps = getTotalInputSteps()

  // 현재 스텝 ID
  const currentStepId = getEnabledStepId(stepIndex)
  const currentStepConfig = STEP_CONFIG[currentStepId]

  // 프로그레스 바에 표시할 스텝 번호 (웰컴, 완료 제외)
  const displayStepNumber = useMemo(() => {
    const inputSteps = enabledSteps.filter((s): s is StepId => s !== STEP_ID.WELCOME && s !== STEP_ID.COMPLETE)
    const currentInputIndex = inputSteps.indexOf(currentStepId)
    return currentInputIndex + 1
  }, [currentStepId, enabledSteps])

  const handleWelcomeTimeout = useCallback(() => {
    setShowWelcomeTransition(false)
    if (stepIndex === 0) setStepIndex(firstInputStepIndex)
  }, [stepIndex, firstInputStepIndex])

  const handleNextStep = () => {
    setStepIndex(prev => {
      let nextIndex = prev + 1
      // 다음 스텝이 건너뛰어야 하는 스텝이면 그 다음으로 이동
      while (nextIndex < enabledSteps.length && shouldSkipStep(getEnabledStepId(nextIndex), signupData)) {
        nextIndex++
      }
      return nextIndex
    })
  }

  const handlePrevStep = useCallback(() => {
    // 첫 번째 입력 스텝(닉네임)에서 뒤로가기 시 TOS 페이지로 이동
    if (stepIndex === firstInputStepIndex) {
      const provider = searchParams.get('provider') || 'kakao'
      const user_id = searchParams.get('user_id') || ''
      navigate(`/auth/tos?provider=${provider}&user_id=${user_id}`)
      return
    }

    setStepIndex(prev => {
      let prevIndex = prev - 1
      // 이전 스텝이 건너뛰어야 하는 스텝이면 그 전으로 이동
      while (prevIndex >= 0 && shouldSkipStep(getEnabledStepId(prevIndex), signupData)) {
        prevIndex--
      }
      return prevIndex
    })
  }, [signupData, stepIndex, navigate, searchParams, firstInputStepIndex])

  const handleNicknameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 9)
    setSignupData(prev => ({ ...prev, nickname: value }))
  }, [])

  const handleGenderChange = useCallback((gender: string) => {
    setSignupData(prev => ({ ...prev, gender }))
  }, [])

  const handleBirthDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignupData(prev => ({ ...prev, birthDate: e.target.value }))
  }, [])

  const handleGradeChange = useCallback((grade: GradeCode | null) => {
    setSignupData(prev => ({
      ...prev,
      grade,
      // 학년 변경 시 학교 정보 초기화
      schoolName: '',
      schoolCode: ''
    }))
    // 학교 선택 상태도 초기화
    setIsSchoolSelected(false)
  }, [])

  const handleSchoolChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSignupData(prev => ({ ...prev, schoolName: e.target.value }))
    // 텍스트 수정 시 선택 상태 해제
    setIsSchoolSelected(false)
  }, [])

  const handleSchoolSelect = useCallback((schoolName: string, schoolCode: string | null) => {
    setSignupData(prev => ({ ...prev, schoolName: schoolName, schoolCode: schoolCode || '' }))
    // 학교 선택 시 선택 상태 활성화
    setIsSchoolSelected(true)
  }, [])

  const handleMbtiChange = useCallback((mbti: string) => {
    setSignupData(prev => ({ ...prev, mbti }))
  }, [])

  const handleVltTypeChange = useCallback((vltType: number) => {
    setSignupData(prev => ({ ...prev, vltType }))
  }, [])

  const nicknameValidation = useMemo(() => validateNickname(signupData.nickname), [signupData.nickname])

  // 스텝별 진행 가능 여부 검사
  const canProceed = useMemo(() => {
    switch (currentStepId) {
      case STEP_ID.NICKNAME:
        return nicknameValidation.valid
      case STEP_ID.GENDER:
        return signupData.gender !== ''
      case STEP_ID.BIRTH_DATE:
        return validateBirthDate(signupData.birthDate)
      case STEP_ID.GRADE:
        return signupData.grade !== null
      case STEP_ID.VLT_TYPE:
        return signupData.vltType !== 0
      case STEP_ID.SCHOOL:
        // 학교를 실제로 선택했을 때만 진행 가능
        return isSchoolSelected
      case STEP_ID.MBTI:
        return signupData.mbti.length === 4
      default:
        return false
    }
  }, [currentStepId, signupData, nicknameValidation, isSchoolSelected])

  // 현재 스텝의 버튼 텍스트
  const currentButtonText = currentStepConfig.buttonText

  // 웰컴 트랜지션 표시 (입력 스텝 진입 시)
  const isInputStep = currentStepId !== STEP_ID.WELCOME && currentStepId !== STEP_ID.COMPLETE
  if (showWelcomeTransition && isInputStep) {
    return <WelcomeScreen onTimeout={() => setShowWelcomeTransition(false)} />
  }

  // 웰컴 화면
  if (currentStepId === STEP_ID.WELCOME) {
    return <WelcomeScreen onTimeout={handleWelcomeTimeout} />
  }

  // 완료 화면
  if (currentStepId === STEP_ID.COMPLETE) {
    return <SignupCompleteScreen onStart={() => onComplete(signupData)} signupData={signupData} />
  }

  // 스텝별 컴포넌트 렌더링
  const renderStepComponent = () => {
    switch (currentStepId) {
      case STEP_ID.NICKNAME:
        return (
          <NicknameStep
            value={signupData.nickname}
            onChange={handleNicknameChange}
            maxLength={9}
            error={nicknameValidation.error}
          />
        )
      case STEP_ID.GENDER:
        return (
          <GenderStep value={signupData.gender} onChange={handleGenderChange} nickname={signupData.nickname} />
        )
      case STEP_ID.BIRTH_DATE:
        return <BirthDateStep value={signupData.birthDate} onChange={handleBirthDateChange} />
      case STEP_ID.GRADE:
        return (
          <GradeStep value={signupData.grade ?? ''} onChange={handleGradeChange} birthDate={signupData.birthDate} />
        )
      case STEP_ID.SCHOOL:
        return (
          <SchoolStep
            schoolName={signupData.schoolName}
            onChange={handleSchoolChange}
            onSelect={handleSchoolSelect}
            grade={signupData.grade}
          />
        )
      case STEP_ID.MBTI:
        return <MbtiStep value={signupData.mbti} onChange={handleMbtiChange} />
      case STEP_ID.VLT_TYPE:
        return <VltTypeStep value={signupData.vltType ?? 0} onChange={(vltType) => handleVltTypeChange(vltType ?? 0)} />  
      default:
        return <div className='text-center'>알 수 없는 단계입니다.</div>
    }
  }

  // 스킵 버튼 표시 여부
  const showSkipButton = currentStepConfig.showSkipButton ?? false

  return (
    <div className='fixed inset-0 z-50 flex flex-col font-sans noto-sans-kr'>
      <SignupProgress
        currentStep={displayStepNumber}
        totalSteps={totalInputSteps}
        onBack={stepIndex >= firstInputStepIndex ? handlePrevStep : undefined}
      />
      <div className='flex-1 px-6 py-8 flex flex-col justify-center'>{renderStepComponent()}</div>
      <div className='px-6 py-4 space-y-3'>
        {showSkipButton && (
          <button
            type='button'
            className='text-sm text-center text-amber-600 w-full hover:underline'
            onClick={handleNextStep}
          >
            다음에 알려줄게!
          </button>
        )}
      </div>
      <div className='px-4 pb-6'>
        <WideButton
          handleClick={handleNextStep}
          buttonText={currentButtonText}
          className={`${!canProceed ? 'bg-gray-300 cursor-not-allowed border-gray-300 text-gray-500' : 'text-white bg-amber-500 border-amber-500'}`}
          isDisabled={!canProceed}
        />
      </div>
    </div>
  )
}
